import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Button, Typography, useTheme, IconButton, Alert, alpha, Grid2 as Grid, Stepper, Step, StepLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import { centeredRowStack } from '@ui/style/style';
import ImportErrorsDialog from './ImportErrorsDialog';
import { toast } from 'react-toastify';
import { useAppDispatch } from '@application/store/useStore';
import { downloadExcelTemplateAsync, uploadExcelAsync, uploadTicketDocumentAsync } from '@application/features/tickets/ticketsThunk';
import { HFilledTextField } from '@ui/components/common/TextField';
const ImportTicketDialog = ({ open, onClose, isSmallScreen, eventId, onSuccess }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [activeStep, setActiveStep] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showErrorsDialog, setShowErrorsDialog] = useState(false);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [fileType, setFileType] = useState('');
    const [formData, setFormData] = useState({});
    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file extension
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (fileExtension !== 'xlsx' && fileExtension !== 'pdf') {
                toast.error(t('sponsorships.tickets.invalidFileFormat') ||
                    'Invalid file format. Only Excel files (.xlsx) or PDF files (.pdf) are allowed.');
                // Reset the input
                event.target.value = '';
                return;
            }
            setSelectedFile(file);
            setFileType(fileExtension);
            setUploadErrors([]);
            if (fileExtension === 'pdf') {
                setFormData({});
            }
        }
    };
    const handleDownloadTemplate = async () => {
        try {
            const result = await dispatch(downloadExcelTemplateAsync()).unwrap();
            // Create a blob from the response data
            const contentType = result.headers['content-type'] ||
                result.headers['Content-Type'] ||
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const blob = new Blob([result.data], { type: contentType });
            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Get filename from Content-Disposition header or use default
            const contentDisposition = result.headers['content-disposition'] || result.headers['Content-Disposition'] || '';
            let filename = 'TicketsTemplate.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                    // Handle URL-encoded filenames
                    try {
                        filename = decodeURIComponent(filename);
                    }
                    catch (e) {
                        // If decoding fails, use the original filename
                    }
                }
            }
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            // Show success message
            toast.success(t('sponsorships.tickets.templateDownloadedSuccessfully') ||
                'Tickets template downloaded successfully!');
        }
        catch (error) {
            console.error('Error downloading template:', error);
            toast.error(error?.message ||
                t('sponsorships.tickets.failedToDownloadTemplate') ||
                'Failed to download template. Please try again.');
        }
    };
    const handleAdd = async () => {
        if (!selectedFile || !eventId) {
            return;
        }
        if (fileType === 'xlsx') {
            setIsUploading(true);
            try {
                const result = await dispatch(uploadExcelAsync({
                    file: selectedFile,
                    eventId: eventId
                })).unwrap();
                // Check if upload was successful
                if (result.isSuccess && result.data) {
                    const successCount = result.data.totalRowsProcessed || 0;
                    const errorCount = result.data.errors?.length || 0;
                    const hasProcessedData = successCount > 0;
                    const hasErrors = errorCount > 0;
                    if (hasProcessedData) {
                        // Refresh the tickets list immediately so user can see added rows
                        if (onSuccess) {
                            onSuccess();
                        }
                        if (hasErrors && result.data.errors) {
                            // Partial success: some rows succeeded, some failed
                            setUploadErrors(result.data.errors);
                            setUploadResult({
                                successCount,
                                errorCount
                            });
                            // Show success toast with count
                            toast.success(t('sponsorships.tickets.partialImportSuccess', { count: successCount }) ||
                                `${successCount} ticket(s) added successfully. ${errorCount} row(s) had errors.`);
                            // Don't close dialog - show summary instead
                            // Don't show error dialog immediately - let user see summary first
                        }
                        else {
                            // All rows succeeded - show success message and close
                            toast.success(result.message ||
                                t('sponsorships.tickets.importAllSuccess', { count: successCount }) ||
                                `${successCount} ticket(s) added successfully!`);
                            // Close the dialog
                            setSelectedFile(null);
                            setUploadResult(null);
                            // Reset file input
                            const fileInput = document.getElementById('tickets-excel-file-input');
                            if (fileInput) {
                                fileInput.value = '';
                            }
                            onClose();
                        }
                    }
                    else {
                        // No data was processed - show errors only
                        const errors = result.data?.errors ||
                            result.errors || [result.message || 'No data was processed.'];
                        setUploadErrors(errors);
                        setUploadResult(null);
                        setShowErrorsDialog(true);
                    }
                }
                else {
                    // Upload failed - check for errors in data first, then fallback to message
                    const errors = result.data?.errors ||
                        result.errors || [result.message || 'Failed to upload Excel file.'];
                    setUploadErrors(errors);
                    setUploadResult(null);
                    setShowErrorsDialog(true);
                }
            }
            catch (error) {
                const errorMessage = error?.message ||
                    error?.error ||
                    t('sponsorships.tickets.importError') ||
                    'Failed to upload Excel file. Please try again.';
                toast.error(errorMessage);
                setUploadErrors([errorMessage]);
                setShowErrorsDialog(true);
            }
            finally {
                setIsUploading(false);
            }
        }
        else if (fileType == 'pdf') {
            setIsUploading(true);
            try {
                const request = {
                    file: selectedFile,
                    eventId: eventId,
                    ticketNo: formData.ticketNo,
                    ticketType: formData.ticketType,
                    venueSection: formData.venueSection,
                    venueSectionRow: formData.venueSectionRow,
                    venueSectionSeat: formData.venueSectionSeat,
                    description: formData.description
                };
                const result = await dispatch(uploadTicketDocumentAsync(request)).unwrap();
                // Show success message
                toast.success(t('sponsorships.tickets.pdfUploadSuccess') || 'PDF document uploaded successfully!');
                // Refresh the tickets list if callback provided
                if (onSuccess) {
                    onSuccess();
                }
                // Reset form and close dialog
                handleCancel();
            }
            catch (error) {
                const errorMessage = error?.message ||
                    error?.error ||
                    t('sponsorships.tickets.pdfUploadError') ||
                    'Failed to upload PDF file. Please try again.';
                toast.error(errorMessage);
            }
            finally {
                setIsUploading(false);
            }
        }
    };
    const handleCancel = () => {
        setActiveStep(0);
        setSelectedFile(null);
        setFileType('');
        setUploadErrors([]);
        setUploadResult(null);
        setShowErrorsDialog(false);
        setFormData({});
        // Reset file input
        const fileInput = document.getElementById('tickets-excel-file-input');
        if (fileInput) {
            fileInput.value = '';
        }
        onClose();
    };
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value === '' ? undefined : value
        }));
    };
    const handleViewErrors = () => {
        setShowErrorsDialog(true);
    };
    const handleCloseAfterErrors = () => {
        setShowErrorsDialog(false);
        setUploadErrors([]);
        setUploadResult(null);
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('tickets-excel-file-input');
        if (fileInput) {
            fileInput.value = '';
        }
        onClose();
    };
    const steps = [
        t('sponsorships.tickets.importPDFs') || 'Import PDFs',
        t('sponsorships.tickets.assignTicketDetails') || 'Assign Ticket Details (Optional)'
    ];
    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };
    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };
    const dialogContent = (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '12px', p: isSmallScreen ? 1 : 2 }, children: [_jsx(Box, { children: _jsx(Stepper, { activeStep: activeStep, alternativeLabel: true, children: steps.map((label, index) => (_jsx(Step, { children: _jsx(StepLabel, { StepIconProps: {
                                sx: {
                                    '&.Mui-active': {
                                        color: theme.palette.primary.main
                                    },
                                    '&.Mui-completed': {
                                        color: theme.palette.success.main
                                    }
                                }
                            }, children: label }) }, label))) }) }), uploadResult && uploadResult.successCount > 0 && uploadResult.errorCount > 0 && (_jsx(Alert, { color: 'warning', variant: 'outlined', sx: {
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                    borderColor: alpha(theme.palette.warning.main, 0.16),
                    color: theme.palette.warning.dark,
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': {
                        padding: 0,
                        width: '100%',
                        flex: 1
                    },
                    '& .MuiAlert-action': {
                        paddingTop: 0,
                        alignItems: 'flex-start',
                        marginRight: 0
                    }
                }, icon: _jsx(Icon, { icon: 'solar:info-circle-bold' }), action: _jsx(Button, { size: 'small', variant: 'outlined', color: 'warning', onClick: handleViewErrors, endIcon: _jsx(Icon, { icon: 'eva:arrow-forward-outline', fontSize: 16 }), sx: {
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                    }, children: t('sponsorships.tickets.errors') || 'Errors' }), children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.5 }, children: [_jsx(Typography, { variant: 'body2', fontWeight: 500, children: t('sponsorships.tickets.partialImportSummary', {
                                success: uploadResult.successCount,
                                errors: uploadResult.errorCount
                            }) ||
                                `${uploadResult.successCount} ticket(s) added successfully. ${uploadResult.errorCount} row(s) had errors.` }), _jsx(Typography, { variant: 'caption', sx: { opacity: 0.8 }, children: t('sponsorships.tickets.viewErrorsHint') ||
                                'Click "Errors" to see details about the failed rows.' })] }) })), activeStep === 0 && (_jsxs(_Fragment, { children: [fileType !== 'pdf' && (_jsx(Alert, { color: 'info', variant: 'outlined', sx: {
                            backgroundColor: alpha(theme.palette.info.main, 0.08),
                            borderColor: alpha(theme.palette.info.main, 0.16),
                            color: theme.palette.info.dark,
                            alignItems: 'center',
                            '& .MuiAlert-message': {
                                padding: 0
                            }
                        }, icon: _jsx(Icon, { icon: 'solar:info-circle-bold' }), action: _jsx(Button, { size: 'small', variant: 'contained', color: 'info', endIcon: _jsx(Icon, { icon: 'eva:cloud-download-outline', fontSize: 18, color: theme.palette.info.contrastText }), onClick: handleDownloadTemplate, children: t('sponsorships.tickets.template') || 'Template' }), children: t('sponsorships.tickets.excelFormatInfo') ||
                            'Please follow the format for the .xlsx file that you are uploading.' })), _jsxs(Box, { sx: {
                            width: '100%',
                            border: `1px solid ${theme.palette.grey[400]}`,
                            borderRadius: '16px',
                            background: theme.palette.common.grey8,
                            padding: 1.5,
                            minHeight: '48px'
                        }, children: [_jsx(Typography, { variant: 'subtitle2', children: t('sponsorships.tickets.uploadFile') || 'Upload File' }), _jsx(Typography, { variant: 'body2', my: 1, children: selectedFile
                                    ? fileType === 'pdf'
                                        ? t('sponsorships.tickets.uploadPdfDescription') || 'Select a PDF file to upload'
                                        : t('sponsorships.tickets.uploadExcelDescription') ||
                                            'Select an Excel file to upload'
                                    : t('sponsorships.tickets.uploadFileDescription') ||
                                        'Select an Excel (.xlsx) or PDF (.pdf) file to upload' }), _jsxs(Box, { sx: {
                                    border: `1px solid ${theme.palette.success.dark}`,
                                    borderRadius: '12px',
                                    width: '100%',
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme.palette.success.dark,
                                    cursor: 'pointer',
                                    position: 'relative'
                                }, onClick: () => {
                                    const fileInput = document.getElementById('tickets-excel-file-input');
                                    if (fileInput) {
                                        fileInput.click();
                                    }
                                }, children: [_jsx("input", { id: 'tickets-excel-file-input', type: 'file', accept: '.xlsx,.pdf', style: { display: 'none' }, onChange: handleFileChange }), _jsx(Box, { sx: {
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            flex: 1,
                                            px: 1,
                                            color: theme.palette.success.dark,
                                            fontSize: 13,
                                            fontWeight: 500
                                        }, children: _jsx(Typography, { variant: 'subtitle2', noWrap: true, sx: { minWidth: 0, maxWidth: '100%' }, children: t('sponsorships.tickets.selectFile') || 'Select File' }) })] })] }), _jsx(Box, { display: 'flex', flexDirection: 'column', gap: '12px', children: selectedFile && (_jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                padding: 1,
                                borderRadius: 1.75,
                                bgcolor: theme.palette.background.neutral
                            }, children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, sx: { minWidth: 0, maxWidth: '100%', flex: 1 }, children: selectedFile.name }), _jsx(IconButton, { size: 'Xs', onClick: (e) => {
                                        e.stopPropagation();
                                        setSelectedFile(null);
                                        setFileType('');
                                        setFormData({});
                                    }, sx: {
                                        padding: 0.5,
                                        border: 'none'
                                    }, children: _jsx(Icon, { icon: 'eva:close-fill', color: theme.palette.action.active }) })] })) })] })), activeStep === 1 && (_jsxs(_Fragment, { children: [_jsx(Alert, { color: 'info', variant: 'outlined', sx: {
                            backgroundColor: alpha(theme.palette.info.main, 0.08),
                            borderColor: alpha(theme.palette.info.main, 0.16),
                            color: theme.palette.info.dark,
                            alignItems: 'center',
                            '& .MuiAlert-message': {
                                padding: 0
                            }
                        }, icon: _jsx(Icon, { icon: 'solar:info-circle-bold' }), children: t('sponsorships.tickets.pdfFormatInfo') ||
                            'Please upload a PDF file. All fields below are optional.' }), selectedFile && (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Typography, { variant: 'subtitle2', sx: { color: theme.palette.text.secondary }, children: t('sponsorships.tickets.optionalTicketDetails') || 'Optional Ticket Details' }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(HFilledTextField, { fullWidth: true, bg: 'grey8', label: t('sponsorships.tickets.ticketNo') || 'Ticket Number', value: formData.ticketNo || '', onChange: (e) => handleInputChange('ticketNo', e.target.value) }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(HFilledTextField, { fullWidth: true, bg: 'grey8', label: t('sponsorships.tickets.ticketType') || 'Ticket Type', value: formData.ticketType || '', onChange: (e) => handleInputChange('ticketType', e.target.value) }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(HFilledTextField, { fullWidth: true, bg: 'grey8', label: t('sponsorships.tickets.venueSection') || 'Venue Section', value: formData.venueSection || '', onChange: (e) => handleInputChange('venueSection', e.target.value) }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(HFilledTextField, { fullWidth: true, bg: 'grey8', label: t('sponsorships.tickets.venueSectionRow') || 'Row', value: formData.venueSectionRow || '', onChange: (e) => handleInputChange('venueSectionRow', e.target.value) }) }), _jsx(Grid, { size: { xs: 12, sm: 6 }, children: _jsx(HFilledTextField, { fullWidth: true, bg: 'grey8', label: t('sponsorships.tickets.venueSectionSeat') || 'Seat', value: formData.venueSectionSeat || '', onChange: (e) => handleInputChange('venueSectionSeat', e.target.value) }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(HFilledTextField, { fullWidth: true, bg: 'grey8', label: t('sponsorships.tickets.description') || 'Description', value: formData.description || '', onChange: (e) => handleInputChange('description', e.target.value), multiline: true, rows: 3 }) })] })] }))] }))] }));
    const actionButtons = (_jsxs(Box, { sx: { ...centeredRowStack }, width: '100%', gap: 1, children: [activeStep === 1 && (_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: handleBack, size: 'large', children: t('actions.back') || 'Back' })), activeStep === 0 && (_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: handleCancel, size: 'large', children: t('actions.cancel') || 'Cancel' })), activeStep === 0 && fileType === 'pdf' && selectedFile ? (_jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', onClick: handleNext, size: 'large', children: t('actions.uploadAndNext') || 'Upload & Next' })) : (_jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', onClick: handleAdd, size: 'large', disabled: !selectedFile || isUploading, children: isUploading
                    ? t('actions.uploading') || 'Uploading...'
                    : fileType === 'pdf'
                        ? t('actions.upload') || 'Upload'
                        : t('actions.addNow') || 'Add now' }))] }));
    return (_jsxs(_Fragment, { children: [!isSmallScreen && (_jsx(CustomDialog, { backgroundVariant: 'white', maxWidth: 'md', open: open, onClose: handleCancel, titleWithIcon: t('sponsorships.tickets.uploadTicket') || 'Upload Ticket', titleIcon: '/assets/images/add-person.png', actions: actionButtons, children: dialogContent })), isSmallScreen && (_jsx(BDrawer, { anchor: 'bottom', title: t('sponsorships.tickets.uploadTicket') || 'Upload Ticket', open: open, onClose: handleCancel, showCloseButton: true, dialogActions: actionButtons, children: _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: dialogContent }) })), _jsx(ImportErrorsDialog, { open: showErrorsDialog, onClose: uploadResult
                    ? handleCloseAfterErrors
                    : () => {
                        setShowErrorsDialog(false);
                        setUploadErrors([]);
                        setSelectedFile(null);
                        onClose();
                    }, onBack: () => {
                    setShowErrorsDialog(false);
                }, isSmallScreen: isSmallScreen, errors: uploadErrors })] }));
};
export default ImportTicketDialog;
